import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Button,
  Cell,
  Column,
  EmptyState,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  SegmentedControl,
  Table,
  TableBody,
  TableHeader,
  Tag,
} from '@wodi/ui';
import { useState } from 'react';

import { can, sessionQuery } from '../api/session.ts';
import { birthRegisterQuery, deathRegisterQuery } from '../api/vital-events.ts';
import { TableScroll } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import styles from './vital-events.module.css';
import { DEATH_STATUS_TAGS, errorText, gramsText, PLACE_WORDS } from './words.ts';

// The deaths and births registers (docs/plans/phase2-mortuary-and-notifications.md §1.1 item 11):
// the newest 100 of each at this facility, for the records office.

function Deaths() {
  const list = useQuery(deathRegisterQuery);
  if (list.isPending) return <LoadingBlock label="Loading the deaths" rows={4} />;
  if (list.isError)
    return (
      <Notice tone="error" action={<Button onPress={() => void list.refetch()}>Try again</Button>}>
        {errorText(list.error, 'The deaths could not be loaded. Try again.')}
      </Notice>
    );
  if (list.data.items.length === 0)
    return (
      <EmptyState>No deaths recorded here. Deaths are recorded from a stay or casualty.</EmptyState>
    );
  return (
    <TableScroll>
      <Table aria-label="Deaths" flat>
        <TableHeader>
          <Column isRowHeader>Patient</Column>
          <Column>Patient no.</Column>
          <Column>Died</Column>
          <Column>Place</Column>
          <Column>Status</Column>
        </TableHeader>
        <TableBody items={list.data.items}>
          {(d) => (
            <Row id={d.id}>
              <Cell>
                <Link to="/deaths/$deathId" params={{ deathId: d.id }}>
                  {d.patient.displayName}
                </Link>
              </Cell>
              <Cell>
                <span className="wodi-id">{d.patient.patientNumber}</span>
              </Cell>
              <Cell>{formatInstant(d.diedAt)}</Cell>
              <Cell>{PLACE_WORDS[d.place]}</Cell>
              <Cell>
                <Tag state={DEATH_STATUS_TAGS[d.status][1]}>{DEATH_STATUS_TAGS[d.status][0]}</Tag>
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

function Births() {
  const list = useQuery(birthRegisterQuery);
  if (list.isPending) return <LoadingBlock label="Loading the births" rows={4} />;
  if (list.isError)
    return (
      <Notice tone="error" action={<Button onPress={() => void list.refetch()}>Try again</Button>}>
        {errorText(list.error, 'The births could not be loaded. Try again.')}
      </Notice>
    );
  if (list.data.items.length === 0)
    return (
      <EmptyState>
        No births recorded here. Births are recorded from the mother&apos;s visit.
      </EmptyState>
    );
  return (
    <TableScroll>
      <Table aria-label="Births" flat>
        <TableHeader>
          <Column isRowHeader>Baby</Column>
          <Column>Mother</Column>
          <Column>Born</Column>
          <Column>Sex</Column>
          <Column>Weight</Column>
          <Column>Status</Column>
        </TableHeader>
        <TableBody items={list.data.items}>
          {(b) => (
            <Row id={b.id}>
              <Cell>
                <Link to="/births/$birthId" params={{ birthId: b.id }}>
                  {b.baby.displayName}
                </Link>
              </Cell>
              <Cell>{b.mother.displayName}</Cell>
              <Cell>{formatInstant(b.bornAt)}</Cell>
              <Cell>{b.sexDisplay}</Cell>
              <Cell>{gramsText(b.birthWeightGrams)}</Cell>
              <Cell>
                <Tag state={DEATH_STATUS_TAGS[b.status][1]}>{DEATH_STATUS_TAGS[b.status][0]}</Tag>
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

export function VitalEventsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const deaths = session !== null && can(session, 'deaths.read');
  const births = session !== null && can(session, 'births.read');
  const [view, setView] = useState<'deaths' | 'births'>(deaths ? 'deaths' : 'births');
  if (!deaths && !births) {
    return (
      <>
        <PageHeader title="Deaths and births" />
        <Notice tone="info">
          You don&apos;t have permission to see the registers. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  return (
    <div className={styles.page}>
      <PageHeader
        title="Deaths and births"
        description="Recorded at this facility, newest first."
      />
      <Section
        title={view === 'deaths' ? 'Deaths' : 'Births'}
        actions={
          deaths && births ? (
            <SegmentedControl
              aria-label="Register"
              value={view}
              onChange={(v) => {
                setView(v === 'births' ? 'births' : 'deaths');
              }}
              options={[
                { id: 'deaths', label: 'Deaths' },
                { id: 'births', label: 'Births' },
              ]}
            />
          ) : undefined
        }
      >
        {view === 'deaths' ? <Deaths /> : <Births />}
      </Section>
    </div>
  );
}
